import React, { useState, useRef } from 'react'
import { Box, Button, TextField } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import ReCAPTCHA from 'react-google-recaptcha'
import { reverse } from 'named-urls'
import { isNil } from 'lodash'

import { PasswordInputField } from 'components'

import useAuth from 'hooks/useAuth'
import useSnackbar from 'hooks/useSnackbar'
import useClientPortalTheme from 'hooks/useClientPortalTheme'

import { getGoogleRecaptchaToken, isProductionOrHomol } from 'service/env'
import constants from 'constants/index'
import schemas from '../../schemas'
import * as service from 'service'
import useStyles from './styles'
import { routes } from 'Routes'

const Form = () => {
  const { PLATFORM_SLUG_ID } = constants.settings

  const [loading, setLoading] = useState(false)
  const [recaptcha, setRecaptcha] = useState(undefined)

  const recaptchaRef = useRef(null)

  const disableSubmitButton = isProductionOrHomol
    ? !recaptcha || loading
    : false

  const auth = useAuth()
  const snackbar = useSnackbar()
  const history = useHistory()
  const { slug } = useParams()

  const { clientPortalTheme } = useClientPortalTheme()
  const classes = useStyles({ clientPortalTheme })()

  const { handleSubmit, errors, control } = useForm({
    validationSchema: schemas.login,
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const hasSlug =
    !isNil(slug) &&
    !constants.settings.DEFAULT_SLUGS_PATHS.includes(slug?.toLowerCase())

  const onSubmit = async (data) => {
    if (disableSubmitButton) return

    try {
      setLoading(true)
      const response = await service.dponet.auth.login({
        ...data,
        ...(hasSlug && { publicPath: slug }),
        recaptcha,
      })

      if (response.status === 202) {
        const accessToken = response.data.accessToken

        history.push({
          pathname: reverse(routes.twoFactorAuthentication, {
            slug: slug || PLATFORM_SLUG_ID,
            accessToken,
          }),
          state: {
            email: data.email,
          },
        })
        return
      }

      await auth.loadDataWithAnimation()
    } catch (error) {
      if (isProductionOrHomol) resetRecaptcha()
      snackbar.open({
        message: defineErrorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const resetRecaptcha = () => {
    recaptchaRef.current.reset()
    setRecaptcha(undefined)
  }

  const defineErrorMessage = (error) => {
    const response = error?.response
    const status = response?.status
    const errorMessage = response?.data?.error?.message

    if ((status === 401 || status === 422) && errorMessage) return errorMessage

    return 'Ocorreu algum erro! Tente novamente!'
  }

  const handleAcceptedRecaptcha = (recaptchaToken) => {
    setRecaptcha(recaptchaToken)
  }
  const disableRecaptcha = () => setRecaptcha(undefined)

  const redirectForgotPassword = () =>
    history.push(
      reverse(routes.forgotPassword, {
        slug: slug || PLATFORM_SLUG_ID,
      }),
    )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        as={
          <TextField
            label="Email"
            type="email"
            color="primary"
            variant="outlined"
            error={!!errors.email}
            helperText={errors?.email?.message}
            fullWidth
          />
        }
        control={control}
        name="email"
        mode="onBlur"
      />
      <Controller
        as={
          <PasswordInputField
            label="Senha"
            type="password"
            variant="outlined"
            margin="normal"
            error={!!errors.password}
            helperText={errors?.password?.message}
            fullWidth
          />
        }
        control={control}
        name="password"
        mode="onBlur"
      />
      <Button
        type="button"
        variant="text"
        className={classes.forgotPasswordLink}
        onClick={redirectForgotPassword}
      >
        Esqueceu sua senha?
      </Button>
      {isProductionOrHomol && (
        <Box mt={2} display="flex" alignItems="center" justifyContent="center">
          <ReCAPTCHA
            ref={recaptchaRef}
            render="explicit"
            sitekey={getGoogleRecaptchaToken()}
            onChange={handleAcceptedRecaptcha}
            onExpired={disableRecaptcha}
          />
        </Box>
      )}
      <Box mt={3}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          className={classes.buttonPrimary}
          disabled={disableSubmitButton}
        >
          {loading ? 'Carregando...' : 'Acessar'}
        </Button>
      </Box>
    </form>
  )
}

export default Form
